/**
 * Shared CEFR helpers (ADR-033 D4).
 *
 * The level axis crosses backend↔mobile, so `CEFRLevel` itself is owned by the
 * AsyncAPI contract and only re-exported here. What lives here is the pair of
 * things every level-rendering surface needs and would otherwise duplicate: the
 * ordered level list and the literal-key name lookup.
 *
 * Extracted from `screens/practice/PracticeComponents.tsx` when the vocabulary
 * screen became the second consumer — a vocab component importing a practice
 * screen module would be the wrong dependency direction.
 */

import type { TFunction } from 'i18next';

import type { CEFRLevel } from '../generated/api/http-contracts';

export type { CEFRLevel };

/**
 * Every level the contract knows about, in order. Deliberately a literal (not
 * derived from `CEFRLevelSchema.options`) so that adding a level to the
 * contract fails `PracticeCefrLevels.test.tsx` and forces someone to author the
 * name + hint copy rather than silently rendering a level with no words for it.
 *
 * Level support ≠ level content: a level with nothing in it renders its empty
 * state, it is never dropped from a selector.
 */
export const CEFR_LEVELS: CEFRLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

/** Position of a level in `CEFR_LEVELS` (A1 = 0 … C2 = 5). */
export function cefrOrdinal(level: CEFRLevel): number {
  return CEFR_LEVELS.indexOf(level);
}

/** Translate a CEFR level using literal keys (required by literal-i18n-keys). */
export function translateCEFRLevel(t: TFunction, level: CEFRLevel): string {
  switch (level) {
    case 'A1': return t('lessons.cefrLevels.a1');
    case 'A2': return t('lessons.cefrLevels.a2');
    case 'B1': return t('lessons.cefrLevels.b1');
    case 'B2': return t('lessons.cefrLevels.b2');
    case 'C1': return t('lessons.cefrLevels.c1');
    case 'C2': return t('lessons.cefrLevels.c2');
  }
}
